import { motion } from "framer-motion";
import { FileText, Video, Inbox, UserCheck, Timer } from "lucide-react";
import { FadeUp, MaskedLines, Chapter } from "@/components/motion";
import { CTAButton } from "@/components/CTA";

const STEPS = [
  {
    num: "01",
    icon: FileText,
    title: "NOTIFICA",
    lead: "Cuéntanos qué ocurre.",
    desc: "Cuando aparece una incidencia física en tu comercio, nos la comunicas con dos cosas: una descripción del problema y un vídeo de la incidencia. Sin formularios eternos ni llamadas en espera.",
  },
  {
    num: "02",
    icon: Inbox,
    title: "RECIBIMOS",
    lead: "Aprea recibe y analiza.",
    desc: "La gestora recibe la información, analiza la incidencia y te responde con la mayor brevedad posible. Desde ese momento, la incidencia ya está en gestión.",
  },
  {
    num: "03",
    icon: UserCheck,
    title: "ASIGNAMOS",
    lead: "Activamos al especialista.",
    desc: "Comunicamos la incidencia al técnico especializado correspondiente. Tú no buscas, no llamas, no comparas disponibilidades.",
  },
  {
    num: "04",
    icon: Timer,
    title: "ACTUAMOS",
    lead: "Comienza la cuenta atrás.",
    desc: "Desde la gestión de la incidencia arranca el compromiso: un técnico especializado en tu comercio en un máximo de 24 horas hábiles. Horario hábil de 08:00 a 20:00. Domingos y festivos no computan.",
  },
];

export default function ComoFunciona() {
  return (
    <main data-testid="page-como-funciona" className="pt-40 lg:pt-48">
      <header className="mx-auto max-w-[1500px] px-6 lg:px-12 pb-24 lg:pb-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-mono text-[11px] tracking-[0.4em] text-glow/80 mb-8"
        >
          EL PROCESO, PASO A PASO
        </motion.p>
        <h1 className="font-display font-800 uppercase leading-[0.95] tracking-tight text-[12vw] sm:text-7xl lg:text-8xl text-offwhite">
          <MaskedLines
            mode="load"
            delay={0.3}
            lines={[
              <span key="a">Cómo</span>,
              <span key="b" className="font-cormorant normal-case font-medium text-metal">funciona.</span>,
            ]}
          />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8 }}
          className="mt-9 max-w-xl text-mist text-base md:text-lg leading-relaxed"
        >
          Cuatro pasos entre la avería y el técnico. Tú solo participas en el primero.
        </motion.p>
      </header>

      <section className="border-t border-silver/10">
        {STEPS.map((step, i) => (
          <div key={step.num} className="border-b border-silver/10" data-testid={`paso-${step.num}`}>
            <div className="mx-auto max-w-[1500px] px-6 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <FadeUp className="lg:col-span-3">
                <span className="font-oswald font-700 text-7xl lg:text-8xl text-outline">{step.num}</span>
              </FadeUp>
              <FadeUp delay={0.1} className="lg:col-span-4">
                <step.icon className="h-6 w-6 text-glow/80 mb-6" strokeWidth={1.5} />
                <h2 className="font-oswald font-600 uppercase text-3xl lg:text-4xl tracking-wide text-offwhite mb-3">
                  {step.title}
                </h2>
                <p className="font-cormorant text-2xl text-silver">{step.lead}</p>
              </FadeUp>
              <FadeUp delay={0.2} className="lg:col-span-5">
                <p className="text-mist leading-relaxed max-w-md">{step.desc}</p>
              </FadeUp>
            </div>
          </div>
        ))}
      </section>

      <section className="bg-abyss border-b border-silver/10 py-24 lg:py-32">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FadeUp>
            <div className="border border-silver/15 bg-panel/40 p-9 h-full">
              <span className="font-mono text-[10px] tracking-[0.35em] text-glow/80 block mb-8">LO QUE ENVÍAS</span>
              <div className="flex items-center gap-5 mb-5">
                <FileText className="h-5 w-5 text-silver" strokeWidth={1.5} />
                <span className="font-oswald uppercase text-xl tracking-wide text-offwhite">Descripción del problema</span>
              </div>
              <div className="flex items-center gap-5">
                <Video className="h-5 w-5 text-silver" strokeWidth={1.5} />
                <span className="font-oswald uppercase text-xl tracking-wide text-offwhite">Vídeo de la incidencia</span>
              </div>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="border border-silver/15 bg-panel/40 p-9 h-full">
              <span className="font-mono text-[10px] tracking-[0.35em] text-glow/80 block mb-8">LO QUE HACE APREA</span>
              <ul className="space-y-4">
                {["Recibe y analiza la incidencia", "Te responde con la mayor brevedad", "Comunica al técnico especializado", "Coordina la actuación en menos de 24h hábiles"].map((t) => (
                  <li key={t} className="flex items-center gap-4 text-mist">
                    <span className="h-px w-6 bg-glow/60" />
                    <span className="text-sm md:text-base">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
        </div>
        <FadeUp delay={0.15} className="mx-auto max-w-[1500px] px-6 lg:px-12 mt-16">
          <CTAButton testid="como-funciona-cta-alta">QUIERO APREA</CTAButton>
        </FadeUp>
      </section>
    </main>
  );
}
